import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { APIProvider } from '@vis.gl/react-google-maps'
import { Loader2, MapPin, type LucideIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAutocompleteSuggestions } from '@/hooks/useAutocompleteSuggestions'
import { COUNTRIES, type Country, regionCodesForCountry } from '@/lib/constants'
import { parsePlaceAddress } from '@/lib/parsePlaceAddress'
import { cn } from '@/lib/utils'

// Column caps enforced by the API on every address (clients / technicians /
// jobs): address varchar(255), city varchar(100), postal_code varchar(16). The
// inputs truncate at these rather than letting the value grow past them.
const ADDRESS_MAX_LENGTH = 255
const CITY_MAX_LENGTH = 100
const POSTAL_CODE_MAX_LENGTH = 16

export interface AddressFieldsChange {
  country?: string
  city?: string
  postalCode?: string
  address?: string
  latitude?: number
  longitude?: number
}

export interface AddressFieldsProps {
  country: string
  city: string
  postalCode: string
  address: string
  onChange: (next: AddressFieldsChange) => void
  errors?: {
    city?: string
    address?: string
    postalCode?: string
    country?: string
  }
  translationNs?: string
  formatCountryLabel?: (country: Country) => string
  className?: string
  controlClassName?: string
  selectControlClassName?: string
  portalContainer?: HTMLElement | null
  // When set, the address/city/postal code/country labels show a required
  // marker (asterisk). Off by default so callers that treat the address as
  // optional are unaffected.
  required?: boolean
  // Field arrangement. 'grid' (default) keeps the legacy 2-column block used by
  // the job/technician forms. 'stacked' renders the client-form design: a
  // full-width Address, then Postal | City, then Country paired with an optional
  // trailing slot, and a MapPin icon inside the Address input.
  layout?: 'grid' | 'stacked'
  // Stacked layout only: content rendered full-width right after the Address
  // field (e.g. an "Additional address details" input).
  afterAddressSlot?: ReactNode
  // Stacked layout only: content rendered next to Country in the same row
  // (e.g. a "Property type" select).
  countryTrailingSlot?: ReactNode
  // Stacked layout only: extra fields rendered in the same flex-wrap row as
  // postal / city / country (e.g. access code and floor).
  stackedWrapSlot?: ReactNode
  // Stacked layout only: render Postal, City and Country in one equal-width
  // row. The default keeps the Client form's two-row arrangement.
  stackedLocationColumns?: 2 | 3
}

const stackedWrapItemClass = 'w-full min-w-[240px] flex-1 basis-[calc(50%-8px)]'
const stackedThreeColumnItemClass =
  'w-full min-w-[180px] flex-1 basis-[calc(33.333%-11px)]'

const fieldInputClass =
  'h-10 rounded-lg px-4 py-3 text-base font-medium placeholder:font-normal focus-visible:ring-0 focus-visible:ring-offset-0'

const selectTriggerClass =
  'h-10 rounded-lg px-4 py-3 text-base font-medium focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 data-[placeholder]:font-normal'

/** Client-form stacked layout (Figma Address card). */
const stackedFieldInputClass =
  'h-10 rounded-xl border-[#e7e9ef] bg-[#f5f6fa] px-4 py-3 text-base font-medium placeholder:font-normal focus-visible:ring-0 focus-visible:ring-offset-0'

const stackedSelectTriggerClass =
  'h-10 rounded-xl border-[#e7e9ef] bg-[#f5f6fa] px-4 py-3 text-base font-medium focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 data-[placeholder]:font-normal'

// Required marker (asterisk) appended to a field label.
function RequiredMark() {
  return (
    <span className='ml-1 text-destructive' aria-hidden='true'>
      *
    </span>
  )
}

const ADDRESS_AUTOCOMPLETE_OPTIONS: Partial<google.maps.places.AutocompleteRequest> =
  {
    includedPrimaryTypes: ['street_address', 'route', 'premise', 'subpremise'],
  }

function CountryField({
  country,
  onChange,
  translationNs,
  formatCountryLabel,
  error,
  required,
  stacked,
  controlClassName,
  selectControlClassName,
  portalContainer,
}: Pick<
  AddressFieldsProps,
  | 'country'
  | 'onChange'
  | 'translationNs'
  | 'formatCountryLabel'
  | 'controlClassName'
  | 'selectControlClassName'
  | 'portalContainer'
> & {
  error?: string
  required?: boolean
  stacked?: boolean
}) {
  const { t } = useTranslation(translationNs)

  return (
    <div className={stacked ? 'space-y-1' : 'space-y-2'}>
      <Label className={stacked ? 'text-xs leading-4' : undefined}>
        {t('form.country')}
        {required && <RequiredMark />}
      </Label>
      <Select
        value={country}
        onValueChange={value => {
          if (!value || value === country) return
          onChange({
            country: value,
            city: '',
            postalCode: '',
            address: '',
            latitude: undefined,
            longitude: undefined,
          })
        }}
      >
        <SelectTrigger
          aria-required={required}
          className={cn(
            stacked ? stackedSelectTriggerClass : selectTriggerClass,
            selectControlClassName ?? controlClassName,
            error && 'border border-destructive'
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent container={portalContainer}>
          {COUNTRIES.map(c => (
            <SelectItem key={c} value={c}>
              {formatCountryLabel ? formatCountryLabel(c) : c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className='text-xs text-destructive'>{error}</p>}
    </div>
  )
}

interface PlacesAutocompleteInputProps {
  id: string
  label: string
  value: string
  placeholder: string
  error?: string
  required?: boolean
  translationNs: string
  requestOptions: Partial<google.maps.places.AutocompleteRequest>
  isActive: boolean
  leadingIcon?: LucideIcon
  inputClassName?: string
  fieldClassName?: string
  labelClassName?: string
  maxLength?: number
  onActivate: () => void
  onValueChange: (value: string) => void
  onPlaceSelected: (
    suggestion: google.maps.places.AutocompleteSuggestion
  ) => void
  onDismiss?: () => void
}

function PlacesAutocompleteInput({
  id,
  label,
  value,
  placeholder,
  error,
  required,
  translationNs,
  requestOptions,
  isActive,
  leadingIcon: LeadingIcon,
  inputClassName,
  fieldClassName,
  labelClassName,
  maxLength,
  onActivate,
  onValueChange,
  onPlaceSelected,
  onDismiss,
}: PlacesAutocompleteInputProps) {
  const { t } = useTranslation(translationNs)
  const [selecting, setSelecting] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const { suggestions, isLoading, isError, resetSession } =
    useAutocompleteSuggestions(
      value,
      requestOptions,
      300,
      isActive && !dismissed
    )

  const showDropdown =
    isActive &&
    !selecting &&
    value.trim().length >= 3 &&
    (isLoading ||
      isError ||
      (!isError &&
        (suggestions.length > 0 || (!isLoading && suggestions.length === 0))))

  const handleSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion
  ) => {
    setSelecting(true)
    try {
      await onPlaceSelected(suggestion)
      resetSession()
      setDismissed(true)
      onDismiss?.()
    } finally {
      setSelecting(false)
    }
  }

  return (
    <div className={fieldClassName ?? 'space-y-2'}>
      <Label htmlFor={id} className={labelClassName}>
        {label}
        {required && <RequiredMark />}
      </Label>
      <div className='relative'>
        {LeadingIcon && (
          <LeadingIcon className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#171a20]' />
        )}
        <Input
          id={id}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-required={required}
          aria-invalid={!!error}
          className={cn(
            inputClassName,
            LeadingIcon && 'pl-9',
            error && 'border border-destructive focus-visible:ring-destructive'
          )}
          onFocus={() => {
            setDismissed(false)
            onActivate()
          }}
          onBlur={() => {
            setDismissed(true)
            onDismiss?.()
          }}
          onChange={e => {
            // Re-activate after a prior selection: select dismisses the field
            // while focus can remain, so typing must turn suggestions back on.
            setDismissed(false)
            onActivate()
            onValueChange(e.target.value)
          }}
          autoComplete='off'
        />
        {(isLoading || selecting) && isActive && (
          <Loader2 className='absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[#6c7285]' />
        )}
        {showDropdown && (
          <div className='absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-md border border-[#e7e9ef] bg-white shadow-md'>
            {isLoading && suggestions.length === 0 && !isError && (
              <p className='px-3 py-2 text-sm font-normal leading-[1.4] text-[#6c7285]'>
                {t('form.addressSuggestionsLoading')}
              </p>
            )}
            {isError && (
              <p className='px-3 py-2 text-sm font-normal leading-[1.4] text-[#6c7285]'>
                {t('form.placesUnavailable')}
              </p>
            )}
            {!isLoading && !isError && suggestions.length === 0 && (
              <p className='px-3 py-2 text-sm font-normal leading-[1.4] text-[#6c7285]'>
                {t('form.addressNoSuggestions')}
              </p>
            )}
            {!isError &&
              suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.placePrediction?.placeId ?? index}
                  type='button'
                  // preventDefault on mousedown so the input does not blur
                  // (and unmount this list) before the selection handler runs.
                  onMouseDown={e => {
                    e.preventDefault()
                    void handleSuggestionClick(suggestion)
                  }}
                  className='flex w-full flex-col items-start px-3 py-2 text-left text-sm font-normal leading-[1.4] text-[#171a20] hover:bg-[#f4f5f8]'
                >
                  {suggestion.placePrediction?.text.text}
                </button>
              ))}
          </div>
        )}
      </div>
      {error && <p className='text-xs text-destructive'>{error}</p>}
    </div>
  )
}

function AddressAutocompleteFields({
  country,
  city,
  postalCode,
  address,
  onChange,
  errors,
  translationNs,
  formatCountryLabel,
  className,
  required,
  layout = 'grid',
  afterAddressSlot,
  countryTrailingSlot,
  stackedWrapSlot,
  controlClassName,
  selectControlClassName,
  portalContainer,
  stackedLocationColumns = 2,
}: AddressFieldsProps & { translationNs: string }) {
  const { t } = useTranslation(translationNs)
  const [isAddressActive, setIsAddressActive] = useState(false)
  const isStacked = layout === 'stacked'

  const regionCodes = useMemo(() => regionCodesForCountry(country), [country])

  const addressRequestOptions = useMemo(
    (): Partial<google.maps.places.AutocompleteRequest> => ({
      ...ADDRESS_AUTOCOMPLETE_OPTIONS,
      includedRegionCodes: regionCodes,
    }),
    [regionCodes]
  )

  const applyPlaceSelection = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!suggestion.placePrediction) return

      const place = suggestion.placePrediction.toPlace()
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
      })

      const parsed = parsePlaceAddress(place, country)
      // Prefer structured street; if Google returns no street parts, fall back
      // to the prediction text so the address field still updates on select.
      const predictionText = suggestion.placePrediction.text?.text?.trim()
      onChange({
        country: parsed.country,
        city: parsed.city,
        postalCode: parsed.postalCode,
        address: parsed.address || predictionText || '',
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      })
    },
    [country, onChange]
  )

  const inputClass = cn(
    isStacked ? stackedFieldInputClass : fieldInputClass,
    controlClassName
  )
  const fieldGapClass = isStacked ? 'space-y-1' : 'space-y-2'

  const addressField = (
    <PlacesAutocompleteInput
      id='address'
      label={t('form.address')}
      value={address}
      placeholder={t('form.addressSearchPlaceholder')}
      error={errors?.address}
      required={required}
      leadingIcon={isStacked && !address ? MapPin : undefined}
      inputClassName={inputClass}
      fieldClassName={isStacked ? 'space-y-1' : undefined}
      labelClassName={isStacked ? 'text-xs leading-4' : undefined}
      maxLength={ADDRESS_MAX_LENGTH}
      translationNs={translationNs}
      requestOptions={addressRequestOptions}
      isActive={isAddressActive}
      onActivate={() => setIsAddressActive(true)}
      onValueChange={next =>
        onChange({
          address: next,
          latitude: undefined,
          longitude: undefined,
        })
      }
      onPlaceSelected={applyPlaceSelection}
      onDismiss={() => setIsAddressActive(false)}
    />
  )

  const cityField = (
    <div className={fieldGapClass}>
      <Label
        htmlFor='city'
        className={isStacked ? 'text-xs leading-4' : undefined}
      >
        {t('form.city')}
        {required && <RequiredMark />}
      </Label>
      <Input
        id='city'
        value={city}
        maxLength={CITY_MAX_LENGTH}
        placeholder={isStacked ? t('form.citySearchPlaceholder') : undefined}
        aria-required={required}
        aria-invalid={!!errors?.city}
        className={cn(
          inputClass,
          errors?.city &&
            'border border-destructive focus-visible:ring-destructive'
        )}
        onChange={e =>
          onChange({
            city: e.target.value,
            latitude: undefined,
            longitude: undefined,
          })
        }
      />
      {errors?.city && (
        <p className='text-xs text-destructive'>{errors.city}</p>
      )}
    </div>
  )

  const postalField = (
    <div className={fieldGapClass}>
      <Label
        htmlFor='postal'
        className={isStacked ? 'text-xs leading-4' : undefined}
      >
        {t('form.postalCode')}
        {required && <RequiredMark />}
      </Label>
      <Input
        id='postal'
        value={postalCode}
        maxLength={POSTAL_CODE_MAX_LENGTH}
        placeholder={isStacked ? t('form.postalPlaceholder') : undefined}
        aria-required={required}
        aria-invalid={!!errors?.postalCode}
        className={cn(
          inputClass,
          errors?.postalCode &&
            'border border-destructive focus-visible:ring-destructive'
        )}
        onChange={e => onChange({ postalCode: e.target.value })}
      />
      {errors?.postalCode && (
        <p className='text-xs text-destructive'>{errors.postalCode}</p>
      )}
    </div>
  )

  const countryField = (
    <CountryField
      country={country}
      onChange={onChange}
      translationNs={translationNs}
      formatCountryLabel={formatCountryLabel}
      error={errors?.country}
      required={required}
      stacked={isStacked}
      controlClassName={controlClassName}
      selectControlClassName={selectControlClassName}
      portalContainer={portalContainer}
    />
  )

  if (isStacked) {
    return (
      <div className={cn('space-y-4', className)}>
        {addressField}
        {afterAddressSlot}
        {stackedLocationColumns === 3 && !countryTrailingSlot ? (
          <div className='flex flex-wrap gap-4'>
            <div className={stackedThreeColumnItemClass}>{postalField}</div>
            <div className={stackedThreeColumnItemClass}>{cityField}</div>
            <div className={stackedThreeColumnItemClass}>{countryField}</div>
          </div>
        ) : (
          <>
            <div className='flex flex-wrap gap-4'>
              <div className={stackedWrapItemClass}>{postalField}</div>
              <div className={stackedWrapItemClass}>{cityField}</div>
            </div>
            <div className='flex flex-wrap gap-4'>
              <div className={stackedWrapItemClass}>{countryField}</div>
              {countryTrailingSlot && (
                <div className={stackedWrapItemClass}>
                  {countryTrailingSlot}
                </div>
              )}
            </div>
          </>
        )}
        {stackedWrapSlot && (
          <div className='flex flex-wrap gap-4'>{stackedWrapSlot}</div>
        )}
      </div>
    )
  }

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', className)}>
      {addressField}
      {cityField}
      {postalField}
      {countryField}
    </div>
  )
}

function ManualAddressFields({
  country,
  city,
  postalCode,
  address,
  onChange,
  errors,
  translationNs,
  formatCountryLabel,
  className,
  required,
  layout = 'grid',
  afterAddressSlot,
  countryTrailingSlot,
  stackedWrapSlot,
  controlClassName,
  selectControlClassName,
  portalContainer,
  stackedLocationColumns = 2,
}: AddressFieldsProps & { translationNs: string }) {
  const { t } = useTranslation(translationNs)
  const isStacked = layout === 'stacked'
  const inputClass = cn(
    isStacked ? stackedFieldInputClass : fieldInputClass,
    controlClassName
  )
  const fieldGapClass = isStacked ? 'space-y-1' : 'space-y-2'
  const showAddressIcon = isStacked && !address

  const addressField = (
    <div className={fieldGapClass}>
      <Label
        htmlFor='address'
        className={isStacked ? 'text-xs leading-4' : undefined}
      >
        {t('form.address')}
        {required && <RequiredMark />}
      </Label>
      <div className='relative'>
        {showAddressIcon && (
          <MapPin className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#171a20]' />
        )}
        <Input
          id='address'
          value={address}
          maxLength={ADDRESS_MAX_LENGTH}
          placeholder={
            isStacked ? t('form.addressSearchPlaceholder') : undefined
          }
          aria-required={required}
          aria-invalid={!!errors?.address}
          className={cn(
            inputClass,
            showAddressIcon && 'pl-9',
            errors?.address &&
              'border border-destructive focus-visible:ring-destructive'
          )}
          onChange={e => onChange({ address: e.target.value })}
        />
      </div>
      {errors?.address && (
        <p className='text-xs text-destructive'>{errors.address}</p>
      )}
    </div>
  )

  const cityField = (
    <div className={fieldGapClass}>
      <Label
        htmlFor='city'
        className={isStacked ? 'text-xs leading-4' : undefined}
      >
        {t('form.city')}
        {required && <RequiredMark />}
      </Label>
      <Input
        id='city'
        value={city}
        maxLength={CITY_MAX_LENGTH}
        placeholder={isStacked ? t('form.citySearchPlaceholder') : undefined}
        aria-required={required}
        aria-invalid={!!errors?.city}
        className={cn(
          inputClass,
          errors?.city &&
            'border border-destructive focus-visible:ring-destructive'
        )}
        onChange={e => onChange({ city: e.target.value })}
      />
      {errors?.city && (
        <p className='text-xs text-destructive'>{errors.city}</p>
      )}
    </div>
  )

  const postalField = (
    <div className={fieldGapClass}>
      <Label
        htmlFor='postal'
        className={isStacked ? 'text-xs leading-4' : undefined}
      >
        {t('form.postalCode')}
        {required && <RequiredMark />}
      </Label>
      <Input
        id='postal'
        value={postalCode}
        maxLength={POSTAL_CODE_MAX_LENGTH}
        placeholder={isStacked ? t('form.postalPlaceholder') : undefined}
        aria-required={required}
        aria-invalid={!!errors?.postalCode}
        className={cn(
          inputClass,
          errors?.postalCode &&
            'border border-destructive focus-visible:ring-destructive'
        )}
        onChange={e => onChange({ postalCode: e.target.value })}
      />
      {errors?.postalCode && (
        <p className='text-xs text-destructive'>{errors.postalCode}</p>
      )}
    </div>
  )

  const countryField = (
    <CountryField
      country={country}
      onChange={onChange}
      translationNs={translationNs}
      formatCountryLabel={formatCountryLabel}
      error={errors?.country}
      required={required}
      stacked={isStacked}
      controlClassName={controlClassName}
      selectControlClassName={selectControlClassName}
      portalContainer={portalContainer}
    />
  )

  if (isStacked) {
    return (
      <div className={cn('space-y-4', className)}>
        {addressField}
        {afterAddressSlot}
        {stackedLocationColumns === 3 && !countryTrailingSlot ? (
          <div className='flex flex-wrap gap-4'>
            <div className={stackedThreeColumnItemClass}>{postalField}</div>
            <div className={stackedThreeColumnItemClass}>{cityField}</div>
            <div className={stackedThreeColumnItemClass}>{countryField}</div>
          </div>
        ) : (
          <>
            <div className='flex flex-wrap gap-4'>
              <div className={stackedWrapItemClass}>{postalField}</div>
              <div className={stackedWrapItemClass}>{cityField}</div>
            </div>
            <div className='flex flex-wrap gap-4'>
              <div className={stackedWrapItemClass}>{countryField}</div>
              {countryTrailingSlot && (
                <div className={stackedWrapItemClass}>
                  {countryTrailingSlot}
                </div>
              )}
            </div>
          </>
        )}
        {stackedWrapSlot && (
          <div className='flex flex-wrap gap-4'>{stackedWrapSlot}</div>
        )}
      </div>
    )
  }

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', className)}>
      {addressField}
      {cityField}
      {postalField}
      {countryField}
    </div>
  )
}

// Shared address block with Google Places Autocomplete on address only.
// Falls back to manual inputs when VITE_GOOGLE_MAPS_API_KEY is unset.
export function AddressFields({
  translationNs = 'jobs',
  ...props
}: AddressFieldsProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

  if (!apiKey) {
    return <ManualAddressFields {...props} translationNs={translationNs} />
  }

  return (
    <APIProvider apiKey={apiKey}>
      <AddressAutocompleteFields {...props} translationNs={translationNs} />
    </APIProvider>
  )
}
